import React from "react";
import {
  Search, Download, FileText, ChevronLeft, ChevronRight, RotateCcw, FileCheck2,
} from "lucide-react";

/** Filterable invoices/receipts table with pagination. Pure presentational —
 *  state, fetching and PDF/reissue actions live in the parent (Admin.jsx).
 *  Extracted to keep Admin.jsx digestible. */
const InvoicesTableSection = ({
  fmtMoney, fmtDate, pkgLabel,
  invoicesRes, invoicesLoading,
  filters, setF, totalPages,
  onDownloadPdf, onReissue,
}) => (
  <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 md:p-8" data-testid="invoices-table">
    <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
      <div>
        <span className="label-uppercase">Faturas & Recibos</span>
        <h2 className="font-display text-2xl md:text-3xl mt-2">Histórico filtrável</h2>
      </div>
      <div className="text-right text-sm">
        <div className="text-[#A29F9B]">Receita do filtro:</div>
        <div className="font-display text-2xl text-[#C5A880]" data-testid="filter-revenue">
          {fmtMoney(invoicesRes.revenue_filtered)}
        </div>
      </div>
    </div>

    {/* Filter bar */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
      <div className="lg:col-span-2 relative">
        <Search className="w-3.5 h-3.5 text-[#A29F9B] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={filters.q}
          onChange={(e) => setF({ q: e.target.value })}
          placeholder="Email, nome, nº fatura, nº recibo..."
          className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#0C0E12] border border-white/10 focus:border-[#C5A880]/50 outline-none text-sm"
          data-testid="filter-search"
        />
      </div>
      <select value={filters.package_id} onChange={(e) => setF({ package_id: e.target.value })}
        className="px-3 py-2.5 rounded-xl bg-[#0C0E12] border border-white/10 outline-none text-sm" data-testid="filter-package">
        <option value="">Todos os planos</option>
        <option value="monthly">Mensal</option>
        <option value="annual">Anual</option>
      </select>
      <input type="date" value={filters.date_from} onChange={(e) => setF({ date_from: e.target.value })}
        className="px-3 py-2.5 rounded-xl bg-[#0C0E12] border border-white/10 outline-none text-sm" data-testid="filter-from" />
      <input type="date" value={filters.date_to} onChange={(e) => setF({ date_to: e.target.value })}
        className="px-3 py-2.5 rounded-xl bg-[#0C0E12] border border-white/10 outline-none text-sm" data-testid="filter-to" />
      <button onClick={() => setF({ q: "", package_id: "", date_from: "", date_to: "", amount_min: "", amount_max: "" })}
        className="px-4 py-2.5 rounded-xl text-sm border border-white/10 hover:border-[#C5A880]/40 transition-colors text-[#A29F9B] hover:text-[#C5A880]"
        data-testid="filter-clear">
        Limpar filtros
      </button>
    </div>

    {/* Table */}
    {invoicesLoading ? (
      <p className="text-[#A29F9B] py-8 text-center">A carregar...</p>
    ) : invoicesRes.rows.length === 0 ? (
      <p className="text-[#A29F9B] py-8 text-center">Nenhuma fatura corresponde aos filtros.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#A29F9B] border-b border-white/10">
              <th className="py-3 pr-4">Data</th>
              <th className="py-3 pr-4">Cliente</th>
              <th className="py-3 pr-4">Plano</th>
              <th className="py-3 pr-4">Fatura</th>
              <th className="py-3 pr-4">Recibo</th>
              <th className="py-3 pr-4 text-right">Montante</th>
              <th className="py-3 pr-4 text-right">PDF</th>
            </tr>
          </thead>
          <tbody>
            {invoicesRes.rows.map((inv) => (
              <tr key={inv.id} className="border-b border-white/5 hover:bg-white/2 transition-colors" data-testid={`admin-invoice-row-${inv.id}`}>
                <td className="py-3 pr-4 text-[#DCD7D2] whitespace-nowrap">{fmtDate(inv.issued_at)}</td>
                <td className="py-3 pr-4">
                  <div className="text-[#F5F2EE]">{inv.user?.name || "—"}</div>
                  <div className="text-xs text-[#A29F9B] truncate max-w-[180px]">{inv.user?.email || "—"}</div>
                </td>
                <td className="py-3 pr-4 text-[#A29F9B]">{pkgLabel(inv.package_id)}</td>
                <td className="py-3 pr-4 text-[#DCD7D2]">{inv.invoice_number || "—"}</td>
                <td className="py-3 pr-4 text-[#DCD7D2]">{inv.receipt_number || inv.number || "—"}</td>
                <td className="py-3 pr-4 text-right font-display text-base">{fmtMoney(inv.amount, inv.currency)}</td>
                <td className="py-3 pr-4 text-right whitespace-nowrap">
                  {inv.moloni_document_type === "invoiceReceipts" ? (
                    <button onClick={() => onDownloadPdf(inv, "fr")}
                      className="inline-flex items-center gap-1 text-xs bg-[#C5A880]/15 border border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/25 px-2.5 py-1 rounded-lg mr-3"
                      title="Download Fatura-Recibo PDF" data-testid={`admin-fr-${inv.id}`}>
                      <FileCheck2 className="w-3 h-3" /> FR
                    </button>
                  ) : (
                    <>
                      {inv.moloni_invoice_id && (
                        <button onClick={() => onDownloadPdf(inv, "invoice")}
                          className="inline-flex items-center gap-1 text-xs text-[#C5A880] hover:underline mr-3"
                          title="Download Fatura PDF" data-testid={`admin-ft-${inv.id}`}>
                          <FileText className="w-3 h-3" /> FT
                        </button>
                      )}
                      <button onClick={() => onDownloadPdf(inv, "receipt")}
                        className="inline-flex items-center gap-1 text-xs text-[#A29F9B] hover:text-[#C5A880] mr-3"
                        title="Download Recibo PDF" data-testid={`admin-rc-${inv.id}`}>
                        <Download className="w-3 h-3" /> RC
                      </button>
                    </>
                  )}
                  {inv.session_id && (
                    <button onClick={() => onReissue(inv)}
                      className="inline-flex items-center gap-1 text-xs text-[#A29F9B] hover:text-[#E37B7B]"
                      title="Re-emitir Fatura-Recibo" data-testid={`admin-reissue-${inv.id}`}>
                      <RotateCcw className="w-3 h-3" /> Re-emitir
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Pagination */}
    {invoicesRes.total > invoicesRes.page_size && (
      <div className="flex items-center justify-between pt-6 border-t border-white/5 mt-6 text-sm">
        <span className="text-[#A29F9B]">
          {(invoicesRes.page - 1) * invoicesRes.page_size + 1} – {Math.min(invoicesRes.page * invoicesRes.page_size, invoicesRes.total)} de {invoicesRes.total}
        </span>
        <div className="flex items-center gap-2">
          <button onClick={() => setF({ page: Math.max(1, invoicesRes.page - 1) })}
            disabled={invoicesRes.page <= 1}
            className="p-2 rounded-lg border border-white/10 hover:border-[#C5A880]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="page-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[#DCD7D2] min-w-[60px] text-center">{invoicesRes.page} / {totalPages}</span>
          <button onClick={() => setF({ page: Math.min(totalPages, invoicesRes.page + 1) })}
            disabled={invoicesRes.page >= totalPages}
            className="p-2 rounded-lg border border-white/10 hover:border-[#C5A880]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="page-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )}
  </section>
);

export default InvoicesTableSection;
